import { useRef, useEffect, useState, type ComponentType } from 'react';
import { Pressable } from './Pressable';
import { TOKENS, FONTS } from './tokens';
import { useMotion, EASE_POP, EASE_SNAP, DURATION } from './motion';
import type { LucideProps } from 'lucide-react';

interface Tab {
  key: string;
  icon: ComponentType<LucideProps>;
  label: string;
}

interface BottomNavProps {
  tabs: Tab[];
  active: string;
  onChange: (key: string) => void;
}

/**
 * BottomNav — one sliding indicator instead of N independently recoloring icons.
 * Same idea as SegmentedControl, applied to primary navigation.
 *
 * INDICATOR POSITIONING: Ref-measured DOM rect, NOT percentage math.
 * The JSX artifact used `left: idx * (100/tabs.length)%` which drifts when
 * the "Log" tab has a different size (it's a larger amber circle in the spec).
 * We measure each tab's actual rect and position the indicator from that.
 */
export function BottomNav({ tabs, active, onChange }: BottomNavProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });
  const { reduced } = useMotion();
  const slideDur = reduced ? 0 : DURATION.slow;

  useEffect(() => {
    const idx = tabs.findIndex((t) => t.key === active);
    if (idx < 0 || !tabRefs.current[idx] || !containerRef.current) {
      setIndicator((p) => ({ ...p, visible: false }));
      return;
    }
    const btnRect = tabRefs.current[idx]!.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    setIndicator({
      left: btnRect.left - containerRect.left + btnRect.width / 2 - 11,
      width: 22,
      visible: true,
    });
  }, [active, tabs]);

  return (
    <div
      ref={containerRef}
      role="navigation"
      aria-label="Main navigation"
      style={{
        position: 'relative',
        display: 'flex',
        background: TOKENS.surface,
        borderTop: `1px solid ${TOKENS.border}`,
        padding: '6px 4px 8px',
      }}
    >
      {/* Sliding indicator */}
      {indicator.visible && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 6,
            left: indicator.left,
            width: indicator.width,
            height: 3,
            borderRadius: 2,
            background: TOKENS.amber,
            transition: `left ${slideDur}ms ${EASE_POP}`,
          }}
        />
      )}

      {tabs.map((t, i) => {
        const activeTab = t.key === active;
        const isLog = t.key === 'log';
        return (
          <Pressable
            key={t.key}
            ref={(el) => { tabRefs.current[i] = el; }}
            role="tab"
            aria-selected={activeTab}
            onClick={() => onChange(t.key)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              paddingTop: 6,
            }}
          >
            <div style={{
              width: isLog ? 34 : 'auto',
              height: isLog ? 34 : 'auto',
              borderRadius: isLog ? 10 : 0,
              background: isLog ? TOKENS.amber : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: `background ${DURATION.normal}ms ${EASE_SNAP}`,
            }}>
              <t.icon
                size={isLog ? 18 : 19}
                color={isLog ? '#0b0b0d' : activeTab ? TOKENS.amberLight : TOKENS.textTertiary}
                strokeWidth={isLog ? 2.5 : 2}
              />
            </div>
            {!isLog && (
              <span style={{
                fontSize: 9.5, fontWeight: 600,
                color: activeTab ? TOKENS.amberLight : TOKENS.textTertiary,
                transition: `color ${DURATION.normal}ms ${EASE_SNAP}`,
                fontFamily: FONTS.body,
              }}>{t.label}</span>
            )}
          </Pressable>
        );
      })}
    </div>
  );
}
